import { Component, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { RemoveCustomField } from 'src/app/project/modules/workspace/workspace.actions';

import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { Modal, ModalService } from '../../../components/modal/modal.service';
import { PromptService } from '../../../components/prompt/prompt.service';
import { CustomFieldsService } from '../custom-fields.service';

import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { getErrorMessage } from 'src/app/project/helpers/database/get-error-message';
import { setChangedWorkspace } from '../../workspace/workspace';
import { ECustomFieldType } from '../custom-field-types-enums';
import { TCustomField } from '../custom-fields.model';
import { TDeleteCustomFieldModalData } from './delete-cstom-field-modal-data';

@Component({
    selector: 'app-delete-custom-field-modal',
    templateUrl: './delete-custom-field-modal.component.html',
    styleUrls: ['./delete-custom-field-modal.component.scss'],
    standalone: false
})
export class DeleteCustomFieldModalComponent implements OnInit, OnDestroy {
  private modal: Modal<TDeleteCustomFieldModalData> = this.modalService.getModal();

  processing = false;
  field: TCustomField;

  workspaceId: string;
  ECustomFieldType = ECustomFieldType;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private customFieldsService: CustomFieldsService,
    private promptService: PromptService,
    private modalService: ModalService,
    private workspaceService: WorkspaceService,
    private activeService: ActiveService,
    private translationPipe: TranslationPipe,
  ) {}

  ngOnInit(): void {
    this.workspaceId = this.activeService.getActiveWorkspaceId();

    this.setDataFromModal();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  delete(): void {
    if (this.processing) {
      return;
    }

    this.processing = true;
    const workspaceId = this.activeService.getActiveWorkspaceId();
    const workspaces = this.workspaceService.getWorkspaces();
    const fieldId = this.modal.data.field.id;

    this.customFieldsService
      .deleteCustomField(workspaceId, fieldId)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          const promptText = this.translationPipe.transform('prompt_field_deleted');

          const workspaceFieldIndex = workspaces[workspaceId].customFields.findIndex(
            (_fieldId) => fieldId === _fieldId,
          );

          if (workspaceFieldIndex > -1) {
            workspaces[workspaceId].customFields.splice(workspaceFieldIndex, 1);
          }

          this.store.dispatch(
            new RemoveCustomField({
              workspaceId: this.workspaceId,
              customFieldId: fieldId,
            }),
          );

          setChangedWorkspace(workspaceId);

          this.promptService.showSuccess(promptText);
          this.modalService.hideModal(false);
        }),
        catchError((error) => {
          if (error.status === EStatusCode.BAD_REQUEST) {
            getErrorMessage(error).then((message) => {
              this.promptService.showError(message);
            });
          } else {
            const promptText = this.translationPipe.transform('prompt_field_deleted_error');

            this.promptService.showError(promptText);
          }

          return of();
        }),
        finalize(() => {
          this.processing = false;
        }),
      )
      .subscribe();
  }

  hideModal(): void {
    this.modalService.hideModal();
  }

  private setDataFromModal(): void {
    this.field = this.modal.data.field;
  }
}
